import type { BadgeStatKey, BadgeStats } from 'features/catalog/badge-definitions';
import { RARITY_ORDER, type RarityName } from 'features/catalog/rarity-config';
import type { CpuDifficulty } from 'features/battle/ai-config';

export const BATTLE_DECK_SIZE = 12;

/** No badge's stat can go past this, however many gains and boosts stack on it. */
export const BADGE_STAT_MAX = 5;
export const BATTLE_STARTING_HAND = 3;
/** Both seats start with the same hand. First-player is offset by `BATTLE_OPENING_ACTIONS` on turn 1. */
export const BATTLE_SECOND_STARTING_HAND = BATTLE_STARTING_HAND;
export const BATTLE_MAX_HAND = 7;
export const BATTLE_ACTIONS_PER_TURN = 2;
/** The player who goes first only gets this many actions on turn 1. Later turns are `BATTLE_ACTIONS_PER_TURN`. */
export const BATTLE_OPENING_ACTIONS = 1;
export const BATTLE_PRESTIGE_TO_WIN = 10;
/** Last round of the battle. After both seats act, the higher Prestige wins even below the target. */
export const BATTLE_MAX_ROUND = 10;
export const BATTLE_PILE_COUNT = 4;

export type PlayerId = 0 | 1;
export type PileIndex = 0 | 1 | 2 | 3;
/** Absolute pile, the pile beside this badge, or the first/last remaining pile. */
export type DisablePileRef = PileIndex | 'left' | 'right' | 'leftmost' | 'rightmost';
/**
 * Every card in a battle is an instance id (see `BattleState.cards`), never a badge id directly,
 * so two copies of one badge stay separate.
 */
export type CardId = string;

/** One card or empty. */
export type PileSlot = CardId | null;

export const PILE_MAX_RARITY = [...RARITY_ORDER].reverse();
export const PILE_LABELS = ['Common', 'Rare', 'Legendary', 'Mythic'] as const;

export type CardFilter =
  | { kind: 'any' }
  | { kind: 'rarity'; rarity: RarityName }
  | { kind: 'not_rarity'; rarity: RarityName }
  /** This rarity or rarer. */
  | { kind: 'min_rarity'; rarity: RarityName }
  | { kind: 'stat'; stat: BadgeStatKey }
  | { kind: 'any_stat'; stats: BadgeStatKey[] }
  | { kind: 'all_stats'; stats: BadgeStatKey[] };

export type BattleCondition =
  | { kind: 'prestige'; state: 'winning' | 'not_winning' | 'last' | 'tied' }
  | { kind: 'neighbor_empty'; side: 'left' | 'right'; unlocked?: boolean }
  /** Your own pile at `pile` has no badge, regardless of whether it is unlocked. */
  | { kind: 'pile_empty'; pile: PileIndex }
  /** The neighboring pile is occupied by a badge matching `filter`. */
  | { kind: 'neighbor_matches'; side: 'left' | 'right'; filter: CardFilter }
  | { kind: 'hand_fewer_than_opponent' }
  | { kind: 'hand_at_most'; count: number }
  | { kind: 'prestige_at_most'; count: number }
  | { kind: 'hand_at_least'; count: number }
  | { kind: 'deck_empty' }
  | { kind: 'neither_deck_empty' }
  | { kind: 'revealed_count'; atLeast: number }
  | { kind: 'more_revealed_than_all' }
  /** Every pile is revealed and none is rarer than `rarity`. */
  | { kind: 'all_piles_max_rarity'; rarity: RarityName }
  /** `atMost` equal to `atLeast` means exactly that many. */
  | { kind: 'board_badges_with_stat'; stat: BadgeStatKey; atLeast: number; atMost?: number }
  | { kind: 'board_rarity'; rarity: RarityName; atLeast: number }
  | { kind: 'all_board_badges_share_stat' }
  | { kind: 'all_board_badges_have_stats'; stats: BadgeStatKey[] }
  | { kind: 'opponent_board_badge'; filter: CardFilter }
  | { kind: 'drawn_rarity'; rarity: RarityName }
  | { kind: 'drawn_matches_source_rarity' }
  | { kind: 'drawn_matches_filter'; filter: CardFilter }
  /** Your last draw matches `filter` and the opponent's last draw does not. */
  | { kind: 'only_self_drawn_matches_filter'; filter: CardFilter }
  | { kind: 'drawn_rarity_unrepresented' }
  | { kind: 'drawn_rarity_represented' }
  | { kind: 'drawn_rarity_matches_opponent_board_badge' }
  | { kind: 'last_two_drawn_same_rarity' }
  | { kind: 'last_two_drawn_share_stat' }
  | { kind: 'last_two_drawn_match_filter'; filter: CardFilter }
  | { kind: 'discarded_rarities_match' }
  | { kind: 'discarded_self_rarity'; rarity: RarityName }
  | { kind: 'discarded_rarity'; who: 'self' | 'opponent'; rarity: RarityName }
  | { kind: 'discarded_self_higher' }
  | { kind: 'discarded_opponent_higher' }
  | { kind: 'discarded_rarity_at_least'; who: 'self' | 'opponent'; rarity: RarityName }
  | { kind: 'discarded_at_least'; count: number; who?: 'self' | 'opponent' | 'all' }
  | { kind: 'empty_piles'; atLeast: number }
  | { kind: 'neighbors_filled' }
  | { kind: 'unbroken_piles'; atLeast: number }
  | { kind: 'fewer_revealed_than_opponent' }
  | { kind: 'hand_distinct_rarities'; atLeast: number }
  | { kind: 'drawn_rarities_match' }
  | { kind: 'effect_revealed' }
  | { kind: 'other_action_this_turn' }
  | { kind: 'first_action' }
  | { kind: 'second_action' }
  | { kind: 'badge_revealed_or_played_this_turn'; filter: CardFilter; excludeSource: true }
  | { kind: 'effect_played_this_turn'; stat: BadgeStatKey; excludeSource: true }
  | { kind: 'returned_own_badge' }
  | { kind: 'stat_gain_reaches'; stat: BadgeStatKey; total: number }
  /** The source badge's printed + gained stat is 0. */
  | { kind: 'source_stat_zero'; stat: BadgeStatKey }
  /** The previous replace-and-activate did not start the new badge's effect. */
  | { kind: 'activation_missed' };

export type BounceDest = 'hand' | 'deck' | 'top' | 'bottom' | 'discard';

export type BattleOp =
  | { kind: 'draw'; count: number; who?: 'self' | 'opponent' | 'all'; from?: 'opponent' }
  | { kind: 'draw_up_to_tied'; max: number }
  | { kind: 'draw_up_to'; size: number; max: number }
  | { kind: 'discard'; who: 'self' | 'opponent' | 'all'; count: number; random: boolean; filter?: CardFilter }
  | { kind: 'give'; count: number }
  /** The target skips `count` actions on their next turn. */
  | { kind: 'skip_actions_next_turn'; count: number; who?: 'self' | 'opponent' }
  /** The actor's next turn has `count` actions. A second play does not raise it further. */
  | { kind: 'set_next_actions'; count: number }
  | { kind: 'ai_takeover'; difficulty: CpuDifficulty }
  | { kind: 'discard_drawn'; count?: number }
  | { kind: 'discard_deck'; count: number }
  | { kind: 'cycle_hand'; extra: number }
  | { kind: 'reveal_if'; filter: CardFilter; play: boolean; first?: boolean }
  | { kind: 'reveal_hand'; filter: CardFilter; play: boolean; first?: boolean; who?: 'self' | 'opponent' }
  | { kind: 'reveal_hand_neighbor'; side: 'left' | 'right'; play: boolean }
  | { kind: 'discard_neighbor'; side: 'left' | 'right' | 'random' }
  /** Move the source badge to an empty legal pile; later ops treat that pile as the source. */
  | { kind: 'move_source' }
  /** Swap the source badge with a badge on the opponent's board; later ops are skipped if no swap happens. */
  | { kind: 'swap_source_with_opponent_board' }
  | { kind: 'discard_board' }
  /** Discards every matching board badge on both boards. */
  | { kind: 'discard_board_badges'; filter: CardFilter }
  | { kind: 'discard_revealed'; count: number; filter?: CardFilter }
  | { kind: 'discard_self_board_badge' }
  | { kind: 'reveal_drawn'; play: boolean }
  | { kind: 'reveal_unrepresented_drawn' }
  | { kind: 'play_other'; count: number }
  | { kind: 'bounce_opponent_board_badge'; filter: CardFilter; to?: BounceDest }
  /** Drops the leftmost remaining pile from both boards. Badges on it are discarded. */
  | { kind: 'remove_leftmost_pile' }
  /** Ongoing: those piles cannot be revealed onto or activated. */
  | { kind: 'disable_piles'; piles: readonly DisablePileRef[] }
  /** Ongoing: matching badges cannot be revealed. Badges already on the board stay. */
  | { kind: 'block_reveal'; filter: CardFilter }
  /** Ongoing: your matching badges immediately Activate once when revealed, if able. */
  | { kind: 'activate_on_reveal'; filter: CardFilter }
  /** Ongoing: all Activate effects can activate without a stat lead. */
  | { kind: 'ignore_stat_lead' }
  /** Ongoing: neighboring badges gain `amount` Prestige when activated. */
  | { kind: 'neighbor_activate_prestige'; amount: number }
  | {
      kind: 'bounce_self_board_badge';
      filter?: CardFilter;
      to?: BounceDest;
      who?: 'self' | 'opponent';
      random?: boolean;
      /** Skip the played badge so "another" cannot pick itself. */
      excludeSource?: true;
    }
  | { kind: 'bounce_source'; to?: BounceDest }
  | { kind: 'discard_source' }
  | { kind: 'bounce_opponent_matching_drawn'; to?: BounceDest }
  | { kind: 'swap_random_hand'; count: number }
  | { kind: 'swap_hand'; give: CardFilter; take: CardFilter }
  | { kind: 'reveal_unique_filter_drawn'; filter: CardFilter; play?: boolean }
  | { kind: 'deck_bottom_drawn'; count?: number }
  | { kind: 'replace_self_from_deck'; play?: boolean }
  | { kind: 'replace_neighbor_from_deck'; side: 'left' | 'right'; rarityOffset: number }
  | { kind: 'exchange_self_with_opponent_hand' }
  /** Shuffles a new card, a random battle badge of `rarity`, into each player's deck. */
  | { kind: 'shuffle_random_into_decks'; rarity: RarityName }
  /** If the actor's `pile` holds a `rarity` badge, discard it and reveal a new random battle badge of `into`. */
  | { kind: 'upgrade_pile_to_random'; pile: PileIndex; rarity: RarityName; into: RarityName }
  /** Every other occupied pile on both boards is discarded and replaced by a new random battle badge of `rarity`. */
  | { kind: 'replace_other_piles'; rarity: RarityName }
  /** Draws a random `rarity` badge from the actor's deck into their hand. */
  | { kind: 'draw_rarity'; rarity: RarityName }
  /** Each player draws a random `rarity` badge from their own deck and reveals it if a pile fits. */
  | { kind: 'reveal_from_decks'; rarity: RarityName }
  | { kind: 'boost_stat'; amount: number; duration: 'next_turn'; stat?: BadgeStatKey; missing?: true }
  /** Name a rarity, then discard every matching badge from `who`'s hand. */
  | { kind: 'discard_named_rarity'; who: 'opponent' }
  | { kind: 'guess_drawn_rarity'; count: number; reward: 'reveal' | 'prestige' }
  | { kind: 'scry_draw'; count: number }
  | { kind: 'shield_badges' }
  /** Ongoing: steal cannot take your Prestige. Activate: until your next turn. */
  | { kind: 'shield_prestige' }
  | { kind: 'offer_swap_drawn' }
  | { kind: 'if'; condition: BattleCondition; then: BattleOp[]; otherwise?: BattleOp[] }
  | {
      kind: 'gain_prestige';
      amount: number | 'nonempty_piles' | 'opponent_empty_unlocked_piles';
      who?: 'self' | 'opponent' | 'all';
    }
  | { kind: 'gain_prestige_per_drawn'; filter: CardFilter }
  | { kind: 'steal_prestige'; amount: number }
  | { kind: 'lose_prestige'; amount: number }
  /** Omit `stat` to let the player choose. */
  | { kind: 'gain_stat'; stat?: BadgeStatKey; amount: number; max?: number }
  | { kind: 'lose_stat'; stat: BadgeStatKey; amount: number }
  /** The opponent's badge in the pile opposite the source gains `stat`. */
  | { kind: 'opposite_gain_stat'; stat: BadgeStatKey; amount: number };

export const BATTLE_EFFECT_TRIGGERS = ['Activate', 'Ongoing', 'On Reveal'] as const;

export type BattleEffectTrigger = (typeof BATTLE_EFFECT_TRIGGERS)[number];

/** Stats-only battle card. Flavor text is the badge description; it cannot be activated. */
export type BattleTowerEffect = {
  stat?: undefined;
  trigger?: undefined;
  text: string;
  ops: [];
};

export type BattleActivateEffect = {
  stat: BadgeStatKey;
  trigger: BattleEffectTrigger;
  text: string;
  ops: BattleOp[];
};

export type BattleEffect = BattleActivateEffect | BattleTowerEffect;

export type BattlePlayer = {
  name: string;
  deck: string[];
  hand: string[];
  /** One badge or empty per pile. */
  piles: [PileSlot, PileSlot, PileSlot, PileSlot];
  discard: string[];
  prestige: number;
  skipActions: number;
  /** Actions above the usual 2 queued for this player's next turn. Older states omit this. */
  extraActions?: number;
  /** True when this seat's queued 12 were all claimed at battle start. Older states omit this. */
  isPure?: boolean;
  /** Added on top of the source badge's printed stats while that badge is on the board. Starts when chosen and lasts through the end of this player's next turn. */
  statBoost: BadgeStats;
  /** Board badge that `statBoost` is added to. */
  statBoostCardId: string | null;
  /** Own turn-ends left before `statBoost` clears. `2` includes the rest of this turn and the next one. */
  statBoostTurnsLeft: number;
  /** Older saved states kept stat gains per player, keyed by badge id. Moved into `BattleState.statGain` on load. */
  statGain?: Record<string, StatDelta>;
  revealedThisTurn: string[];
  playedThisTurn: string[];
  /** Draw, reveal, and activate actions spent this turn. Effect draws and free activations do not count. */
  actionsTaken: number;
  /** Opponent effects cannot discard or return this player's badges. */
  badgeShield: boolean;
  /** Opponent effects cannot steal this player's Prestige. */
  prestigeShield: boolean;
};

export type BattleContinuation = {
  actor: PlayerId;
  sourceCardId: string;
  sourcePile: PileIndex | null;
  remainingOps: BattleOp[];
  lastDrawn: string[];
  lastDrawnOther: string[];
  lastRevealed: string[];
  lastDiscarded: [string | null, string | null];
  /** Badges each player actually discarded during this effect. */
  discardedCount: [number, number];
  playOtherLeft: number;
  playedThisChain: string[];
  /** Set when this effect returns one of the actor's own board badges. */
  returnedOwnBadge: boolean;
  /** Set when a replace-and-activate did not start the new badge's effect. */
  activationMissed: boolean;
  /** Set when this effect's stat boost actually raised a stat, with the new total. */
  statGainReached: { stat: BadgeStatKey; total: number } | null;
  /** This activation spent one of the actor's actions. */
  consumedAction: boolean;
  revealQueue: string[];
  revealPlayer: PlayerId;
  playAfterReveal: boolean;
};

export type BattleChoice =
  | {
      type: 'choose_pile';
      player: PlayerId;
      cardId: string;
      piles: PileIndex[];
      purpose: 'reveal' | 'move';
      /** Hand badges the player picked `cardId` from; clicking one re-picks instead of placing. */
      options?: string[];
    }
  | {
      type: 'choose_hand';
      player: PlayerId;
      cardIds: string[];
      count: number;
      purpose: 'discard' | 'reveal' | 'give' | 'bottom';
    }
  | {
      type: 'choose_board_badge';
      player: PlayerId;
      owner: PlayerId;
      piles: PileIndex[];
      purpose: 'play_other' | 'bounce' | 'bounce_self' | 'discard_self' | 'swap_source';
      to?: BounceDest;
      who?: 'self' | 'opponent';
    }
  | { type: 'choose_rarity'; player: PlayerId; rarities: RarityName[]; purpose: 'opponent_hand' }
  | {
      type: 'choose_rarity';
      player: PlayerId;
      rarities: RarityName[];
      purpose: 'drawn';
      count: number;
      reward: 'reveal' | 'prestige';
    }
  | { type: 'choose_deck_card'; player: PlayerId; cardIds: string[] }
  | { type: 'choose_stat'; player: PlayerId; stats: BadgeStatKey[]; amount: number; gain?: true }
  | { type: 'choose_swap_drawn'; player: PlayerId; selfCard: string; foeCard: string };

export type BattlePhase = 'turn' | 'choice' | 'game_over';

/** Both boards right after an fx happened, so a view can step through an opponent's move. */
export type BattleFxView = Pick<BattleState, 'players' | 'cards' | 'statGain' | 'removedLeftPiles' | 'pileCount'>;

export type BattleFx = {
  kind:
    | 'discard'
    | 'return'
    | 'draw'
    | 'play'
    | 'exchange'
    | 'reveal'
    | 'upgrade'
    | 'shuffle'
    | 'stat'
    | 'prestige'
    | 'swap'
    | 'remove_pile';
  player: PlayerId;
  /** Empty for `prestige`, which is about a player rather than a card. */
  cardId: string;
  to?: BounceDest;
  pile?: PileIndex;
  /** `stat`: the stat that changed. */
  stat?: BadgeStatKey;
  /** `discard`: a badge revealed onto this badge's pile pushed it off, so it goes with that reveal. */
  replaced?: true;
  /** `discard` / `return` from the board: a player chose this badge, rather than chance or the effect's own rule. */
  picked?: true;
  /** `stat` and `prestige`: signed change. */
  amount?: number;
  view?: BattleFxView;
};

/** An Ongoing (or other) effect that contributed to this log line, shown under the Activate. */
export type BattleLogEffect = {
  cardId: string;
  stat: BadgeStatKey;
  trigger: BattleEffectTrigger;
  text: string;
};

export type BattleLogEntry = {
  kind: 'play' | 'reveal' | 'draw' | 'score' | 'round' | 'info';
  text: string;
  player?: PlayerId;
  cardId?: string;
  stat?: BadgeStatKey;
  trigger?: BattleEffectTrigger;
  effectText?: string;
  details?: string[];
  causedBy?: BattleLogEffect[];
};

/** Permanent changes to a card's stats. Negative when a stat was lost. */
export type StatDelta = Record<BadgeStatKey, number>;

export type BattleState = {
  players: [BattlePlayer, BattlePlayer];
  /** Card instance id → the badge that card is right now. An id missing here is a badge id itself (older saved states). */
  cards?: Record<CardId, string>;
  /** Permanent stat changes, keyed by card instance. They stay with the card wherever it goes, whoever owns it. */
  statGain?: Record<CardId, StatDelta>;
  activePlayer: PlayerId;
  /** Seat that opens round 1 and every round after it. */
  openingPlayer: PlayerId;
  round: number;
  actionsLeft: number;
  turnsTakenThisRound: number;
  rng: number;
  pending: BattleChoice | null;
  stack: BattleContinuation[];
  phase: BattlePhase;
  log: BattleLogEntry[];
  winner: PlayerId | 'tie' | null;
  /** Seat that conceded, when the match ended that way. Missing on older saved states. */
  concededBy?: PlayerId | null;
  /** Seat the CPU plays. `null` is a person vs person battle. */
  cpu: PlayerId | null;
  /** Difficulty the CPU opponent plays at. Missing on older saved states (treat as easy). */
  cpuDifficulty?: CpuDifficulty;
  /** Both seats are driven by the CPU (simulations and tests). */
  autoplay: boolean;
  /** Seats an effect handed to the CPU for the rest of the battle, with the difficulty it plays at. Missing on older saved states. */
  autopilot?: Partial<Record<PlayerId, CpuDifficulty>>;
  fx: BattleFx[];
  /** Card ids whose On Reveal already resolved: `true` if it fired, `false` if it missed. Older states omit this. */
  onRevealFired?: Record<CardId, boolean>;
  /** Set while the badge just played waits on its player's choice and nothing hidden or random has happened yet. */
  undo?: BattleUndo | null;
  /** Visible / legal piles. Missing on older saved states and real battles (treat as 4). */
  pileCount?: number;
  /** How many piles have been cut off the left. Missing on older saved states (treat as 0). */
  removedLeftPiles?: number;
};

/** The state from before `player` played the badge on `pile`, restored by clicking that badge again. */
export type BattleUndo = {
  player: PlayerId;
  pile: PileIndex;
  state: BattleState;
};

export type BattleTarget =
  | { kind: 'pile'; pile: PileIndex }
  | { kind: 'hand'; cardId: string }
  | { kind: 'board_badge'; owner: PlayerId; pile: PileIndex }
  | { kind: 'rarity'; rarity: RarityName }
  | { kind: 'deck_card'; cardId: string }
  | { kind: 'stat'; stat: BadgeStatKey }
  | { kind: 'swap_drawn'; swap: boolean };

export type BattleInput =
  | { type: 'draw' }
  | { type: 'reveal'; cardId: string; pile: PileIndex }
  | { type: 'play'; pile: PileIndex }
  | { type: 'pass' }
  | { type: 'undo' }
  | { type: 'resolve'; target: BattleTarget };
